/**
 * Batch A6 (2026-03-02) — Microsoft Clarity SPA glue. PREVIEW ONLY.
 *
 * Clarity is injected ONCE in public/index.html (<head>). This module
 * layers the two things we can't express in raw HTML:
 *
 *   1. Global CSS-selector mask list — inputs the app renders in many
 *      places (email, phone, OTP, CV area) so masking is uniform even
 *      when a route forgets to add `data-clarity-mask`.
 *   2. Per-route enable/disable — /bmi-ops (admin console) and
 *      employer candidate drawers show real candidate PII, so we
 *      stop recording while they're on-screen and resume when the
 *      user navigates away.
 *
 * Public API:
 *   - `bootstrapClarityMasks()` — call once from App root.
 *   - `syncClarityForRoute(pathname)` — call on every location change.
 *   - `pauseClarityForDrawer(open: boolean)` — call when the drawer
 *     opens / closes (drawers are not routes).
 *
 * All calls are best-effort: if Clarity is blocked, still loading,
 * or has failed, every wrapper here is a no-op — Clarity failure
 * NEVER breaks the app.
 */

function _clarity() {
    if (typeof window === "undefined") return null;
    return typeof window.clarity === "function" ? window.clarity : null;
}

// The CSS selectors below are the umbrella net. Individual components
// ALSO add `data-clarity-mask="true"` on their own containers — this
// layer catches anything a caller forgets.
const GLOBAL_MASK_SELECTORS = [
    // Any text-y candidate input
    'input[type="email"]',
    'input[type="tel"]',
    'input[type="password"]',
    'input[autocomplete="one-time-code"]',
    'input[inputmode="numeric"][maxlength="6"]',
    'input[name*="phone" i]',
    'input[name*="email" i]',
    'input[name*="ctc" i]',
    'input[name*="city" i]',
    'input[name*="company" i]',
    // File pickers — the filename itself is PII (candidate CV names).
    'input[type="file"]',
    // Any element marked by the app (defence in depth).
    "[data-clarity-mask='true']",
    ".clarity-mask",
];

let _masksApplied = false;

export function bootstrapClarityMasks() {
    const c = _clarity();
    if (!c) {
        // Clarity not ready yet — retry when the script eventually loads.
        // We poll for up to ~10s. If it never loads (blocked, network),
        // this quietly stops.
        let tries = 0;
        const t = setInterval(() => {
            tries += 1;
            if (_clarity()) {
                clearInterval(t);
                bootstrapClarityMasks();
            } else if (tries > 20) {
                clearInterval(t);
            }
        }, 500);
        return;
    }
    if (_masksApplied) return;
    _masksApplied = true;
    // Configure the mask list. `set/mask` accepts CSS selectors and
    // covers every existing + future match. See:
    // https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-api
    try {
        GLOBAL_MASK_SELECTORS.forEach((sel) => {
            try { c("set", "mask", sel); } catch { /* ignore per-selector */ }
        });
    } catch {
        /* Clarity throwing must NEVER break the app */
    }
}

// Routes on which we do NOT want session replay at all.
const _EXCLUDED_ROUTE_RE = /^\/(bmi-ops)(\/|$)/;

let _recordingStopped = false;

export function syncClarityForRoute(pathname) {
    const c = _clarity();
    if (!c || typeof pathname !== "string") return;
    const shouldStop = _EXCLUDED_ROUTE_RE.test(pathname);
    if (shouldStop && !_recordingStopped) {
        try { c("stop"); } catch { /* ignore */ }
        _recordingStopped = true;
    } else if (!shouldStop && _recordingStopped) {
        // No public "start" — restart the tag once by re-invoking
        // `consent` (documented restart trigger). If unsupported the
        // next Clarity-script boot on hard reload picks it back up.
        try { c("start"); } catch { /* older versions have no start */ }
        _recordingStopped = false;
    }
}

// Employer candidate drawers open OVER the pipeline route (not a
// separate URL). Called by CandidateDrawer.jsx when it mounts/unmounts.
let _drawerStopped = false;
export function pauseClarityForDrawer(open) {
    const c = _clarity();
    if (!c) return;
    if (open && !_drawerStopped) {
        try { c("stop"); } catch { /* ignore */ }
        _drawerStopped = true;
    } else if (!open && _drawerStopped && !_recordingStopped) {
        try { c("start"); } catch { /* ignore */ }
        _drawerStopped = false;
    }
}
